import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useApi } from "../utils/Api";
import { useCart } from "../context/CartContext";
import { useCheckout } from "../context/CheckoutContext";
import ErrorModal from "./ErrorModal";
import SuccessModal from "./SuccessModal";
import ConfirmationModal from "./ConfirmationModal";

// Same protein classification used in the admin panel (OrderDetails.js) —
// these option ids are the "main" proteins; everything else in item.proteins
// is an "extra" protein.
const MAIN_PROTEIN_IDS = [103, 104, 105, 106, 107, 108];

const SpinnerOverlay = () => (
  <div className="fixed inset-0 bg-black/50 z-[10000] flex justify-center items-center">
    <div className="w-10 h-10 border-4 border-brand-orange border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const OrderHistory = ({ user, closeProfile }) => {
  const { populateCartFromReorder } = useCart();
  const { openCheckout } = useCheckout();
  const { apiFetch } = useApi();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [archiveOrderId, setArchiveOrderId] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const containerRef = React.useRef(null);
  // Caches in-flight/completed receipt PDF generation per order, keyed by
  // order id, so Share can be pre-generated in the background as soon as
  // an order is expanded rather than only when the button is tapped — see
  // prepareReceiptPdf below for why this matters on iOS.
  const receiptPdfCacheRef = useRef({});

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 20;

  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const data = await apiFetch("get-customer-orders", { customer_id: user.id });
        if (isMounted) {
          if (data.status === "success" && Array.isArray(data.orders)) {
            setOrders(data.orders);
          } else {
            setOrders([]);
          }
        }
      } catch (err) {
        console.error("Failed to load orders:", err);
        if (isMounted) setOrders([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchOrders();
    return () => { isMounted = false; };
  }, [user?.id, apiFetch]);

  // Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      if (containerRef.current) {
        const topOffset = 180; 
        const elementPosition = containerRef.current.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - topOffset,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const toggleOrder = (orderId) => {
    const opening = expandedOrderId !== orderId;
    setExpandedOrderId(opening ? orderId : null);
    if (opening) {
      // Start generating the share PDF now, in the background, instead of
      // waiting for the Share tap — see prepareReceiptPdf for why. This
      // must run as a plain side effect here, not inside the setState
      // updater above (React/StrictMode expects updater functions to be
      // pure — kicking off async work from one is unreliable).
      prepareReceiptPdf(orderId, `L-${orderId}`).catch(() => {}); // real errors surface at share-time
    }
  };

  const checkStockBeforeReorder = async (orderItems) => {
    try {
      const activeItems = (orderItems || []).filter((item) => !(Number(item.is_removed) === 1 || item.is_removed === true));
      const data = await apiFetch("check-stock", { cartItems: activeItems });
      if (!data || data.success !== true) return true;
      if (data.soldOutItems?.length > 0) {
        // remaining is null for items flagged unavailable (e.g. marked sold
        // out by staff), where quoting a count would confuse the customer.
        const names = data.soldOutItems
          .map((item) =>
            item.remaining == null
              ? `${item.item_name} (unavailable)`
              : `${item.item_name} (Only ${item.remaining} left)`,
          )
          .join(", ");
        setErrorMessage(<>The following items are unavailable or have insufficient stock: <b>{names}</b>.<br />Please reduce their quantity or remove them from your basket to proceed.</>);
        return false;
      }
      return true;
    } catch (err) { console.warn("Stock check error:", err); return true; }
  };

  const reorder = async (order) => {
    setReorderingId(order.id);
    try {
      const activeOrderItems = (order.items || []).filter((item) => !(Number(item.is_removed) === 1 || item.is_removed === true));
      if (!activeOrderItems.length) { setErrorMessage("This order has no active items available to reorder."); setReorderingId(null); return; }
      const orderForReorder = { ...order, items: activeOrderItems };
      const newCartItems = await populateCartFromReorder(orderForReorder);
      const canReorder = await checkStockBeforeReorder(newCartItems);
      if (!canReorder) { setReorderingId(null); return; }
      closeProfile();
      openCheckout(null);
    } catch (err) { console.error("Reorder failed:", err); } finally { setReorderingId(null); }
  };

  const archiveOrder = async (orderId) => {
    try {
      const data = await apiFetch("archive-order", { order_id: orderId });
      if (data.success) { setOrders((prevOrders) => prevOrders.filter((o) => o.id !== orderId)); }
      else { console.warn("Failed to archive order:", data.message); }
    } catch (err) { console.error("API error:", err); }
  };

  // Does the actual work: fetch the receipt, render it off-screen, and
  // rasterize it into a PDF blob. Pulled out of shareReceipt so it can be
  // kicked off early (on expand) instead of only at share-time.
  const generateReceiptPdfBlob = async (orderId, orderNumber) => {
    let iframe;
    let iframeUrl;
    try {
      const res = await apiFetch("get-receipt-html", { order_id: orderId });
      if (!res?.success || !res?.html) throw new Error(res?.message || "Failed to load receipt.");

      // Render the receipt into a hidden, isolated iframe (same approach as
      // Print) so its own <style> block doesn't leak onto the live page,
      // then rasterize that iframe's body into a PDF.
      // Forced/OS dark-mode can invert this page's colors (background AND
      // text) since it never declares its own color-scheme. Real browser
      // printing always ignores forced dark mode (why Print looks right),
      // but an on-screen html2canvas capture doesn't — so declare a light
      // color-scheme directly in the markup, before the browser ever
      // parses/paints it, rather than patching styles after the fact
      // (which only affects half the inverted colors, too late).
      const lightModeOverride = `<meta name="color-scheme" content="light only"><style>:root{color-scheme:light only !important;}</style>`;
      const html = res.html.replace(/<head[^>]*>/i, (m) => `${m}${lightModeOverride}`);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      iframeUrl = URL.createObjectURL(blob);
      iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-9999px";
      iframe.style.width = "320px";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      // Mobile PDF viewers (iOS Quick Look, Android's built-in viewer,
      // WhatsApp/Telegram previews, etc.) often don't respect the embedded
      // "fit to page" hint for an unusual custom page size, so our narrow
      // 80mm receipt page was opening zoomed in until the user manually
      // pinched out. Using a standard A4 page — with the receipt content
      // centered on it at its normal size — is what these viewers actually
      // handle correctly by default.
      const CONTENT_WIDTH_MM = 120;
      const A4_WIDTH_MM = 210;
      const A4_HEIGHT_MM = 297;
      const MARGIN_TOP_MM = 15;
      const MARGIN_SIDE_MM = (A4_WIDTH_MM - CONTENT_WIDTH_MM) / 2;

      const pdfBlob = await new Promise((resolve, reject) => {
        iframe.onload = async () => {
          try {
            const doc = iframe.contentDocument;
            // The receipt's own CSS has no explicit background color, so
            // forced/OS dark-mode can invert it to a black background when
            // captured on-screen by html2canvas (real browser printing
            // always ignores forced dark mode, which is why Print looks
            // right but this didn't). Force light mode explicitly so the
            // capture matches the real printed look.
            doc.documentElement.style.colorScheme = "light";
            doc.body.style.backgroundColor = "#ffffff";
            // Wait for webfonts + a moment for layout (and border/line
            // positions) to settle before html2canvas measures anything —
            // capturing too early causes divider lines to render offset by
            // a row, cutting through text. A plain timeout (not
            // requestAnimationFrame) on purpose: rAF can be throttled to
            // near-zero or paused entirely once this pre-generation is
            // kicked off in the background and the tab isn't in the
            // foreground the whole time — which stalled this for a full
            // minute in testing.
            if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
            await new Promise((r) => setTimeout(r, 50));

            // Size the iframe to the receipt's real rendered height so
            // html2canvas isn't working against a 0px viewport.
            const contentHeight = doc.body.scrollHeight;
            iframe.style.height = `${contentHeight}px`;

            // Scale 1.5 (not 2) keeps text sharp while meaningfully cutting
            // render time and output size — both matter on mobile, where
            // iOS Safari can reject navigator.share() if too long has
            // passed since the user's tap.
            const canvas = await html2canvas(doc.body, { scale: 1.5, backgroundColor: "#ffffff" });

            // A4's usable height (minus top/bottom margin) sets how much
            // logical-pixel content fits per page.
            const mmPerLogicalPx = CONTENT_WIDTH_MM / doc.body.scrollWidth;
            const maxPageHeightPx = (A4_HEIGHT_MM - MARGIN_TOP_MM * 2) / mmPerLogicalPx;

            // Walk the receipt's direct children (brand, meta, each .item,
            // totals, footer) to find page-break points that fall between
            // elements rather than through one.
            const children = Array.from(doc.body.children);
            const boundaries = [0];
            let pageStartPx = 0;
            for (const child of children) {
              const childBottomPx = child.offsetTop + child.offsetHeight;
              if (childBottomPx - pageStartPx > maxPageHeightPx && child.offsetTop > pageStartPx) {
                boundaries.push(child.offsetTop);
                pageStartPx = child.offsetTop;
              }
            }
            boundaries.push(contentHeight);

            const pxToCanvas = canvas.width / doc.body.scrollWidth;
            const mmPerCanvasPx = CONTENT_WIDTH_MM / canvas.width;

            let pdf;
            for (let i = 0; i < boundaries.length - 1; i++) {
              const sliceHeightPx = Math.round((boundaries[i + 1] - boundaries[i]) * pxToCanvas);
              const sliceHeightMM = sliceHeightPx * mmPerCanvasPx;

              const pageCanvas = document.createElement("canvas");
              pageCanvas.width = canvas.width;
              pageCanvas.height = sliceHeightPx;
              const pageCtx = pageCanvas.getContext("2d");
              // A new canvas starts transparent, and any part of it a
              // rounding mismatch leaves undrawn (or a drawImage read just
              // past the source canvas's edge) stays transparent — which
              // renders as solid black once flattened to JPEG (no alpha
              // channel). Fill white first so any such gap is invisible.
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
              pageCtx.drawImage(canvas, 0, Math.round(boundaries[i] * pxToCanvas), canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

              if (i === 0) {
                pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
              } else {
                pdf.addPage("a4", "portrait");
              }
              // JPEG at high quality is dramatically smaller than PNG for
              // this mostly-white/text content (the earlier PNG version
              // was ~1.7MB for a single-item receipt) — faster to encode
              // and share, which matters for iOS Safari's tight window
              // between a tap and calling navigator.share().
              pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.92), "JPEG", MARGIN_SIDE_MM, MARGIN_TOP_MM, CONTENT_WIDTH_MM, sliceHeightMM);
            }

            resolve(pdf.output("blob"));
          } catch (e) { reject(e); }
        };
        iframe.src = iframeUrl;
      });

      return pdfBlob;
    } finally {
      if (iframe) document.body.removeChild(iframe);
      if (iframeUrl) URL.revokeObjectURL(iframeUrl);
    }
  };

  // Returns a cached in-flight/completed generation for this order, or
  // starts one. Called both eagerly (on expand) and at share-time — if the
  // eager call already finished, share-time just gets the resolved value
  // back immediately, keeping navigator.share() close enough to the user's
  // tap for iOS Safari to allow it (it rejects the call outright if too
  // long has passed since the tap — generating the PDF from scratch at
  // share-time was consistently too slow for that window).
  const prepareReceiptPdf = (orderId, orderNumber) => {
    const cache = receiptPdfCacheRef.current;
    if (!cache[orderId]) {
      cache[orderId] = generateReceiptPdfBlob(orderId, orderNumber).catch((err) => {
        delete cache[orderId]; // let a later attempt retry instead of reusing a rejected promise
        throw err;
      });
    }
    return cache[orderId];
  };

  const shareReceipt = async (orderId, orderNumber) => {
    setReceiptLoading(true);
    try {
      const pdfBlob = await prepareReceiptPdf(orderId, orderNumber);
      const file = new File([pdfBlob], `Lasgidi-Receipt-${orderNumber}.pdf`, { type: "application/pdf" });
      const shareData = {
        title: `Lasgidi Receipt Order ${orderNumber}`,
        text: `Here's your receipt from Lasgidi Restaurant (Order ${orderNumber}).`,
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else {
        setErrorMessage("Sharing isn't supported on this browser/device.");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setErrorMessage("Failed to share receipt.");
      }
    } finally {
      setReceiptLoading(false);
    }
  };

  const printReceipt = async (orderId, orderNumber) => {
    setReceiptLoading(true);
    const oldTitle = document.title;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      const res = await apiFetch("get-receipt-html", { order_id: orderId });
      if (!res?.success || !res?.html) { setErrorMessage(res?.message || "Failed to load receipt."); return; }
      document.title = `Lasgidi Receipt Order ${orderNumber}`;
      const blob = new Blob([res.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      if (isMobile || isIOS) {
        const w = window.open(url, "_blank");
        if (!w) { setErrorMessage("Popup blocked. Please allow popups to print receipts."); URL.revokeObjectURL(url); return; }
        w.onload = () => { try { w.focus(); w.print(); } catch (e) {} };
        setTimeout(() => URL.revokeObjectURL(url), 60000); return;
      }
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed"; iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
      document.body.appendChild(iframe);
      iframe.onload = () => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 500); };
      iframe.src = url;
    } catch (err) { console.error(err); setErrorMessage("Failed to load receipt."); } finally { setTimeout(() => { document.title = oldTitle; setReceiptLoading(false); }, 800); }
  };

  if (loading) return <p className="text-center py-5 text-gray-500 font-['Prata',serif]">Loading orders...</p>;
  if (!orders.length) {
    return <p className="text-center py-10 text-gray-400 text-[15px] font-['Prata',serif]">You haven’t placed any orders yet.</p>;
  }

  return (
    <div ref={containerRef}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {currentOrders.map((order) => {
          const isExpanded = expandedOrderId === order.id;
          return (
            <div
              key={order.id}
              className={`group rounded-2xl border transition-all duration-500 overflow-hidden ${
                isExpanded 
                  ? "bg-[#161616] border-[#fa9324]/40 shadow-[0_20px_50px_rgba(0,0,0,0.3)]" 
                  : "bg-[#0d0d0d] border-[#1a1a1a] hover:border-[#333] hover:shadow-[0_10px_30px_rgba(0,0,0,0.2)]"
              }`}
            >
              {/* Header / Summary */}
              <div 
                className="p-4 sm:p-6 cursor-pointer flex items-center justify-between gap-4 relative overflow-hidden"
                onClick={() => toggleOrder(order.id)}
              >
                {/* Subtle highlight gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#fa9324]/0 to-[#fa9324]/0 group-hover:from-[#fa9324]/[0.02] transition-all pointer-events-none" />

                <div className="flex items-center gap-4 relative z-10 flex-1">
                  {/* Icon - Desktop Only */}
                  <div className={`hidden sm:flex w-14 h-14 rounded-2xl items-center justify-center shrink-0 border-2 transition-all duration-300 ${
                    isExpanded 
                      ? "bg-[#fa9324]/10 border-[#fa9324]/30 text-[#fa9324]" 
                      : "bg-[#1a1a1a] border-[#222] text-[#555] group-hover:border-[#fa9324]/20 group-hover:text-[#888]"
                  }`}>
                    <i className={`fas ${
                      order.order_type === 'Delivery' 
                        ? 'fa-truck' 
                        : (order.order_type === 'Pick Up' || order.order_type === 'Take Away')
                          ? 'fa-shopping-bag'
                          : 'fa-utensils'
                    } text-xl`} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1 sm:mb-1">
                      <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-[#888] group-hover:text-[#aaa] transition-colors whitespace-nowrap">
                        {new Date(order.created_at).toLocaleDateString("en-GB")}
                      </span>
                      <span className="text-[10px] sm:text-[12px] px-2 py-0.5 rounded-full bg-[#1a1a1a] border border-[#333] text-[#ccc] font-mono font-bold">
                        #{order.order_number || order.id}
                      </span>
                      {/* Order Type Badge - Visible on all screens */}
                      <span className={`text-[9px] sm:text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                        order.order_type === 'Delivery' ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                      }`}>
                        {order.order_type}
                      </span>
                    </div>
                    
                    <div className="flex flex-col sm:block gap-1 sm:gap-0">
                      <h3 className="text-white font-bold text-[15px] sm:text-xl tracking-tight leading-tight pr-2">
                        Order L-{order.id}
                      </h3>
                      <div className="flex items-center gap-3">
                        <p className={`hidden max-[420px]:block font-black text-base tracking-tight transition-colors ${isExpanded ? "text-white" : "text-white/90"}`}>
                          £{parseFloat(order.total).toFixed(2)}
                        </p>
                        {(() => {
                          const earnedValue = order.loyalty_points_earned;
                          if (earnedValue === null || earnedValue === undefined) return null;
                          const earned = parseFloat(earnedValue);
                          if (isNaN(earned)) return null;

                          return (
                            <div className="hidden max-[420px]:flex items-center gap-1">
                              <span className="text-[10px] font-bold text-[#fa9324] uppercase tracking-wider">
                                £{earned.toFixed(2)}
                              </span>
                              <i className="fas fa-coins text-[8px] text-[#fa9324]/60" />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-6 relative z-10 shrink-0">
                  <div className="text-right">
                    <p className="max-[420px]:hidden block text-[9px] text-[#888] uppercase tracking-[0.1em] mb-1 font-black">Total Paid</p>
                    <p className="max-[420px]:hidden block font-black text-base sm:text-xl tracking-tight transition-colors text-white">
                      £{parseFloat(order.total).toFixed(2)}
                    </p>
                    {/* Points Earned Badge - Pure Database Driven */}
                    {(() => {
                      const earnedValue = order.loyalty_points_earned;
                      if (earnedValue === null || earnedValue === undefined) return null;
                      const earned = parseFloat(earnedValue);
                      if (isNaN(earned)) return null;

                      return (
                        <div className="max-[420px]:hidden flex items-center justify-end gap-1.5 mt-1">
                          <span className="text-[10px] font-bold text-[#fa9324] uppercase tracking-wider">
                            £{earned.toFixed(2)}
                          </span>
                          <i className="fas fa-coins text-[9px] text-[#fa9324]/60" />
                        </div>
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center transition-all hover:bg-rose-500/20 hover:border-rose-500/40"
                      title="Archive order"
                      onClick={(e) => { e.stopPropagation(); setArchiveOrderId(order.id); }}
                    >
                      <i className="fas fa-trash-alt text-[10px] sm:text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 sm:px-5 pb-3 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="h-px bg-gradient-to-r from-transparent via-[#333] to-transparent mb-3" />
                  
                  {/* Delivery Info */}
                  {order.delivery_address && (
                    <div className="mb-3 text-[13px]">
                      <span className="text-[#888]">Delivery Address: </span>
                      <span className="text-white">{order.delivery_address}</span>
                    </div>
                  )}

                  {/* Items List */}
                  <div className="mb-3">
                    {order.items.map((item) => {
                      const optionTotal = [
                        ...(item.selectedOptionalOptions || []),
                        ...(item.swallowOptions || []),
                        ...(item.proteins || []),
                      ].reduce((sum, opt) => sum + opt.price * opt.quantity, 0);

                      const itemTotal = (parseFloat(item.base_price || 0) + parseFloat(item.required_option_price || 0) + optionTotal) * (item.quantity || 1);
                      const isRemoved = Number(item.is_removed) === 1 || item.is_removed === true;

                      return (
                        <div key={item.id} className={`py-1.5 border-b border-[#222] last:border-0 ${isRemoved ? "opacity-40" : ""}`}>
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <h4 className="text-white font-bold text-[16px] tracking-tight">
                                {item.name} <span className="text-[#999] font-normal ml-1">(£{parseFloat(item.base_price || 0).toFixed(2)})</span>
                              </h4>
                              
                              {/* Options, grouped with a heading line per category (like admin) */}
                              <div className="mt-0.5 space-y-1.5">
                                {[
                                  { type: 'Swallow', options: item.swallowOptions || [] },
                                  {
                                    type: 'Protein(s)',
                                    options: (item.proteins || []).filter(p => MAIN_PROTEIN_IDS.includes(Number(p.id))),
                                  },
                                  {
                                    type: 'Extra',
                                    options: [
                                      ...(item.proteins || []).filter(p => !MAIN_PROTEIN_IDS.includes(Number(p.id))),
                                      ...(item.selectedOptionalOptions || []),
                                    ],
                                  },
                                ].filter(group => group.options.length > 0).map((group, idx) => (
                                  <div key={idx}>
                                    <p className="text-white text-[11px] font-semibold tracking-wide">{group.type}:</p>
                                    <ul className="mt-0.5 pl-3.5 list-disc space-y-0.5">
                                      {group.options.map((opt, i) => (
                                        <li key={i} className="text-[#bbb] text-[13px] flex items-center gap-2">
                                          {opt.name}
                                          {opt.price > 0 && <span className="text-[#888] font-mono ml-auto">+£{opt.price.toFixed(2)}</span>}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <span className="text-[#ccc] text-[15px] font-bold font-['Prata',serif]">
                              x {item.quantity} = <span className="text-white">£{itemTotal.toFixed(2)}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Charges Summary */}
                  <div className="border-t border-[#222] pt-3 space-y-1.5 mb-6">
                    {order.delivery_fee > 0 && (
                      <div className="flex justify-between text-[16px] text-white">
                        <span>Delivery Fee</span>
                        <span>£{parseFloat(order.delivery_fee).toFixed(2)}</span>
                      </div>
                    )}
                    {order.service_charge > 0 && (
                      <div className="flex justify-between text-[16px] text-white">
                        <span>Service Charge</span>
                        <span>£{parseFloat(order.service_charge).toFixed(2)}</span>
                      </div>
                    )}
                    {(parseFloat(order.loyalty_points_used) > 0 || parseFloat(order.loyalty_discount) > 0) && (
                      <div className="flex justify-between text-[16px] text-white font-bold">
                        <span>Loyalty Discount</span>
                        <span>-£{parseFloat(order.loyalty_points_used || order.loyalty_discount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions Container */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div className="flex gap-2">
                      <button 
                        className="flex-1 h-12 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                        disabled={receiptLoading} 
                        onClick={(e) => { e.stopPropagation(); printReceipt(order.id, `L-${order.id}`); }}
                      >
                        <i className="fas fa-print text-xs" /> Print
                      </button>
                      <button
                        className="flex-1 h-12 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                        disabled={receiptLoading}
                        onClick={(e) => { e.stopPropagation(); shareReceipt(order.id, `L-${order.id}`); }}
                      >
                        <i className="fas fa-share-nodes text-xs" /> Share
                      </button>
                    </div>

                    <div className="flex gap-2">
                      {order.tracking_url && (
                        <a 
                          href={order.tracking_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex-1 h-12 rounded-xl bg-[#222] border border-[#333] text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#2a2a2a] transition-all no-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fas fa-map-marker-alt text-xs" /> Track
                        </a>
                      )}
                      <button
                        className="flex-1 h-12 rounded-xl bg-[#fa9324] text-black text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all"
                        disabled={reorderingId === order.id}
                        onClick={(e) => { e.stopPropagation(); reorder(order); }}
                      >
                        <i className="fas fa-redo-alt text-xs" />
                        {reorderingId === order.id ? "Adding..." : "Re-order"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-6 mt-10 pb-6">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-[#333] bg-[#1a1a1a] text-[#888] hover:border-[#fa9324]/50 hover:text-[#fa9324] disabled:opacity-20 disabled:cursor-not-allowed transition-all"
          >
            <i className="fas fa-chevron-left text-xs"></i>
          </button>
          
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#555]">Page</span>
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#fa9324] text-black text-sm font-black shadow-[0_0_15px_rgba(250,147,36,0.3)]">
              {currentPage}
            </span>
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#555]">of {totalPages}</span>
          </div>

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-[#333] bg-[#1a1a1a] text-[#888] hover:border-[#fa9324]/50 hover:text-[#fa9324] disabled:opacity-20 disabled:cursor-not-allowed transition-all"
          >
            <i className="fas fa-chevron-right text-xs"></i>
          </button>
        </div>
      )}

      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      {successMessage && <SuccessModal message={successMessage} onClose={() => setSuccessMessage(null)} />}
      <ConfirmationModal
        isOpen={!!archiveOrderId}
        onClose={() => setArchiveOrderId(null)}
        onConfirm={async () => {
          setArchiving(true);
          await archiveOrder(archiveOrderId);
          setArchiving(false);
          setArchiveOrderId(null);
        }}
        title="Archive Order"
        message="Are you sure you want to archive this order? This will hide it from your order history."
        confirmText="Archive"
        type="danger"
      />
      {(archiving || reorderingId || receiptLoading) && <SpinnerOverlay />}
    </div>
  );
};

export default OrderHistory;